import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ClipboardList, CheckCircle2, User, Briefcase, ListChecks, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/lib/ui";

const STEPS = [
  { key: "contact", label: "Primary Contact", icon: User },
  { key: "project", label: "Project Details", icon: Briefcase },
  { key: "service", label: "Service Requested", icon: ListChecks },
  { key: "consent", label: "Consent & Sign", icon: ShieldCheck },
];

const EMPTY_CONTACT = {
  first_name: "", last_name: "", organization_name: "", job_title: "",
  email: "", phone: "", preferred_contact_method: "email",
  best_time_to_contact: "", decision_authority: "",
};
const EMPTY_PROJECT = {
  project_name: "", brief_description: "", main_goal: "",
  desired_outcome: "", requested_start_date: "", desired_completion_date: "",
  has_deadline: false, deadline_date: "",
  priority: "normal", additional_notes: "",
};
const EMPTY_SERVICE = { services: [], other_description: "", details: "" };
const EMPTY_CONSENT = {
  info_accurate: false, authorized_contact: false, no_guarantee_acknowledged: false,
  preferred_communication: [], signature_full_name: "", agreed: false,
};

export default function IntakeWizard() {
  const navigate = useNavigate();
  const { currentOrgId } = useAuth();
  const [step, setStep] = useState(0);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [project, setProject] = useState(EMPTY_PROJECT);
  const [service, setService] = useState(EMPTY_SERVICE);
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiFetch("/intake/service-options").then((r) => setServiceOptions(r.services || [])).catch(() => {});
  }, []);

  const canContinue = useMemo(() => {
    if (step === 0) {
      return !!(contact.first_name.trim() && contact.last_name.trim() && contact.email.trim() && contact.phone.trim());
    }
    if (step === 1) {
      return !!(project.project_name.trim() && project.brief_description.trim() && project.main_goal.trim());
    }
    if (step === 2) {
      if (service.services.length === 0) return false;
      if (service.services.includes("Other") && !service.other_description.trim()) return false;
      return true;
    }
    if (step === 3) {
      return consent.info_accurate && consent.authorized_contact
        && consent.no_guarantee_acknowledged && consent.agreed
        && consent.signature_full_name.trim().length > 0;
    }
    return false;
  }, [step, contact, project, service, consent]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await apiFetch("/intake/multistep", {
        method: "POST",
        body: JSON.stringify({
          organization_id: currentOrgId,
          primary_contact: contact,
          project_details: {
            ...project,
            requested_start_date: project.requested_start_date || null,
            desired_completion_date: project.desired_completion_date || null,
            deadline_date: project.has_deadline ? (project.deadline_date || null) : null,
          },
          service_request: service,
          consent,
        }),
      });
      toast.success("Intake submitted — client, task, and timeline created.");
      navigate(`/app/clients/${r.client_id}?tab=intake`);
    } catch (e) {
      const msg = typeof e.message === "string" ? e.message : JSON.stringify(e.message);
      toast.error(msg || "Could not submit intake");
    } finally { setSubmitting(false); }
  };

  const toggleService = (s) => {
    setService((cur) => cur.services.includes(s)
      ? { ...cur, services: cur.services.filter((x) => x !== s) }
      : { ...cur, services: [...cur.services, s] });
  };

  const togglePreferredComm = (v) => {
    setConsent((cur) => cur.preferred_communication.includes(v)
      ? { ...cur, preferred_communication: cur.preferred_communication.filter((x) => x !== v) }
      : { ...cur, preferred_communication: [...cur.preferred_communication, v] });
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="intake-wizard">
      <PageHeader title="New intake" subtitle="Guide the client through the 4-step intake in one flow. Everything auto-links back to the client record." />

      {/* Stepper */}
      <ol className="flex items-center gap-2 flex-wrap">
        {STEPS.map((s, i) => {
          const active = i === step;
          const done = i < step;
          const Icon = s.icon;
          return (
            <li key={s.key} data-testid={`step-indicator-${i}`} className="flex-1 min-w-[150px]">
              <button onClick={() => i < step && setStep(i)} disabled={i > step}
                className={`w-full text-left rounded-2xl border p-3 transition-all ${
                  active ? "bg-white border-[#E8712C] shadow-sm"
                  : done ? "bg-[#E8F0E4] border-[#D3E2CD] cursor-pointer hover:opacity-80"
                  : "bg-white border-[#E8E2D9] opacity-70"
                }`}>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#57534E]">
                  {done ? <CheckCircle2 size={14} className="text-[#2C3A29]" /> : <Icon size={14} strokeWidth={1.75} />}
                  Step {i + 1}
                </div>
                <div className={`mt-0.5 text-sm font-semibold ${active ? "text-[#1C1917]" : "text-[#57534E]"}`}>{s.label}</div>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
        {step === 0 && <PrimaryContactStep value={contact} onChange={setContact} />}
        {step === 1 && <ProjectDetailsStep value={project} onChange={setProject} />}
        {step === 2 && <ServiceRequestStep value={service} onChange={setService} onToggle={toggleService} options={serviceOptions} />}
        {step === 3 && <ConsentStep value={consent} onChange={setConsent} onTogglePref={togglePreferredComm} contact={contact} />}

        <div className="flex justify-between mt-8 pt-6 border-t border-[#E8E2D9]">
          <Button variant="outline" className="rounded-full" disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="wizard-back">
            <ChevronLeft size={14} className="mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!canContinue} onClick={() => setStep((s) => s + 1)}
              className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" data-testid="wizard-next">
              Save & Continue <ChevronRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button disabled={!canContinue || submitting} onClick={submit}
              className="rounded-full bg-[#2C3A29] hover:bg-[#3E4C38]" data-testid="wizard-submit">
              {submitting ? "Submitting…" : "Submit intake"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- STEP 1 ---- */
function PrimaryContactStep({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5">
      <StepIntro icon={User} title="Primary Contact" subtitle="Who is the main point of contact for this intake?" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First name" required><Input data-testid="pc-first-name" value={value.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
        <Field label="Last name" required><Input data-testid="pc-last-name" value={value.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
        <Field label="Organization / Company"><Input data-testid="pc-org" value={value.organization_name} onChange={(e) => set("organization_name", e.target.value)} /></Field>
        <Field label="Job title / Role"><Input data-testid="pc-title" value={value.job_title} onChange={(e) => set("job_title", e.target.value)} /></Field>
        <Field label="Email" required><Input data-testid="pc-email" type="email" value={value.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Phone" required><Input data-testid="pc-phone" type="tel" value={value.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="Preferred contact method">
          <Select value={value.preferred_contact_method || "email"} onValueChange={(v) => set("preferred_contact_method", v)}>
            <SelectTrigger data-testid="pc-preferred"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="text">Text</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Best time to contact"><Input data-testid="pc-best-time" placeholder="e.g. Weekdays 9am–5pm ET" value={value.best_time_to_contact} onChange={(e) => set("best_time_to_contact", e.target.value)} /></Field>
      </div>
      <Field label="Is this person authorized to make decisions regarding this request?">
        <div className="flex flex-wrap gap-4 pt-1">
          {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "not_sure", l: "Not sure" }].map((o) => (
            <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="da" value={o.v} checked={value.decision_authority === o.v}
                onChange={() => set("decision_authority", o.v)} className="accent-[#E8712C]"
                data-testid={`pc-authority-${o.v}`} />
              {o.l}
            </label>
          ))}
        </div>
      </Field>
    </div>
  );
}

/* ---- STEP 2 ---- */
function ProjectDetailsStep({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5">
      <StepIntro icon={Briefcase} title="Project Details" subtitle="Tell us what you need help with." />
      <Field label="Project / Request name" required><Input data-testid="pd-name" value={value.project_name} onChange={(e) => set("project_name", e.target.value)} /></Field>
      <Field label="Brief description" required subtitle="Tell us what you need help with.">
        <Textarea rows={3} data-testid="pd-brief" value={value.brief_description} onChange={(e) => set("brief_description", e.target.value)} />
      </Field>
      <Field label="What is the main goal of this project/request?" required subtitle="What problem are you trying to solve?">
        <Textarea rows={3} data-testid="pd-goal" value={value.main_goal} onChange={(e) => set("main_goal", e.target.value)} />
      </Field>
      <Field label="Desired outcome" subtitle="What would you like to accomplish?">
        <Textarea rows={2} data-testid="pd-outcome" value={value.desired_outcome} onChange={(e) => set("desired_outcome", e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Requested start date"><Input type="date" data-testid="pd-start" value={value.requested_start_date} onChange={(e) => set("requested_start_date", e.target.value)} /></Field>
        <Field label="Desired completion date"><Input type="date" data-testid="pd-complete" value={value.desired_completion_date} onChange={(e) => set("desired_completion_date", e.target.value)} /></Field>
      </div>
      <Field label="Is there a deadline?">
        <div className="flex gap-4 pt-1">
          {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
            <label key={String(o.v)} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="hd" checked={value.has_deadline === o.v}
                onChange={() => set("has_deadline", o.v)} className="accent-[#E8712C]"
                data-testid={`pd-hasdeadline-${String(o.v)}`} />
              {o.l}
            </label>
          ))}
        </div>
      </Field>
      {value.has_deadline && (
        <Field label="Deadline date"><Input type="date" data-testid="pd-deadline" value={value.deadline_date} onChange={(e) => set("deadline_date", e.target.value)} /></Field>
      )}
      <Field label="Priority">
        <Select value={value.priority || "normal"} onValueChange={(v) => set("priority", v)}>
          <SelectTrigger data-testid="pd-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Additional requirements or notes">
        <Textarea rows={3} data-testid="pd-notes" value={value.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} />
      </Field>
      <p className="text-[11px] text-[#A8A29E]">File/document upload happens after the client's record is created — you'll be able to attach files from the client profile.</p>
    </div>
  );
}

/* ---- STEP 3 ---- */
function ServiceRequestStep({ value, onChange, onToggle, options }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const opts = options.length ? options : [
    "Administrative Support", "Virtual Assistant Services", "Client Intake",
    "Scheduling", "Calendar Management", "Data Entry", "Document Management",
    "Email Management", "Customer/Client Support", "Research",
    "Project/Task Support", "Other",
  ];
  return (
    <div className="space-y-5">
      <StepIntro icon={ListChecks} title="Service Requested" subtitle="What service are you requesting? Pick as many as apply." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {opts.map((o) => {
          const checked = value.services.includes(o);
          return (
            <label key={o} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
              checked ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9] hover:border-[#A8A29E]"
            }`}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                className="w-4 h-4 accent-[#E8712C]" data-testid={`svc-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              <span className="text-sm">{o}</span>
            </label>
          );
        })}
      </div>
      {value.services.includes("Other") && (
        <Field label='If "Other," please describe' required>
          <Input data-testid="svc-other" value={value.other_description} onChange={(e) => set("other_description", e.target.value)} />
        </Field>
      )}
      <Field label="Please describe what you need us to do">
        <Textarea rows={4} data-testid="svc-details" value={value.details} onChange={(e) => set("details", e.target.value)} placeholder="Share as much detail as helps us route this correctly." />
      </Field>
    </div>
  );
}

/* ---- STEP 4 ---- */
function ConsentStep({ value, onChange, onTogglePref, contact }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  useEffect(() => {
    // pre-fill signature with the primary contact name if empty
    if (!value.signature_full_name && (contact.first_name || contact.last_name)) {
      onChange({ ...value, signature_full_name: `${contact.first_name} ${contact.last_name}`.trim() });
    }
    // eslint-disable-next-line
  }, [contact.first_name, contact.last_name]);
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="space-y-5">
      <StepIntro icon={ShieldCheck} title="Consent & Authorization" subtitle="Please review and confirm before submitting." />
      <div className="space-y-3">
        <Consent testid="consent-accurate" checked={value.info_accurate} onChange={(v) => set("info_accurate", v)}
          label="I confirm that the information I have provided is accurate to the best of my knowledge." />
        <Consent testid="consent-authorized" checked={value.authorized_contact} onChange={(v) => set("authorized_contact", v)}
          label="I authorize ClientDesk and my provider to contact me regarding this service request using the contact information provided above." />
        <Consent testid="consent-noguarantee" checked={value.no_guarantee_acknowledged} onChange={(v) => set("no_guarantee_acknowledged", v)}
          label="I understand that submitting this intake form is a request for service and does not by itself guarantee that services will be provided." />
      </div>
      <Field label="Preferred communication for follow-up">
        <div className="flex flex-wrap gap-4 pt-1">
          {[{ v: "email", l: "Email" }, { v: "phone", l: "Phone" }, { v: "text", l: "Text" }].map((o) => (
            <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={value.preferred_communication.includes(o.v)}
                onChange={() => onTogglePref(o.v)} className="w-4 h-4 accent-[#E8712C]"
                data-testid={`consent-pref-${o.v}`} />
              {o.l}
            </label>
          ))}
        </div>
      </Field>
      <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E2D9] p-4">
        <div className="text-xs uppercase tracking-widest text-[#57534E] mb-3">Electronic signature</div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
          <Field label="Full name">
            <Input data-testid="consent-sig-name" value={value.signature_full_name}
              onChange={(e) => set("signature_full_name", e.target.value)} placeholder="Type your full name" />
          </Field>
          <Field label="Date">
            <Input value={today} readOnly className="bg-white text-[#57534E]" />
          </Field>
        </div>
        <label className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-white border border-[#E8E2D9] cursor-pointer">
          <input type="checkbox" checked={value.agreed} onChange={(e) => set("agreed", e.target.checked)}
            className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid="consent-agree" />
          <span className="text-sm">I agree to the above statements and understand that typing my name serves as my electronic signature.</span>
        </label>
      </div>
      <p className="text-[11px] text-[#A8A29E]">If your organization handles protected health information or requires custom legal language, replace this default consent copy in Settings.</p>
    </div>
  );
}

function Field({ label, required, subtitle, children }) {
  return (
    <div>
      <Label className="text-sm">{label}{required && <span className="text-[#E8712C]"> *</span>}</Label>
      {subtitle && <div className="text-[11px] text-[#A8A29E] mb-1">{subtitle}</div>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Consent({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-white cursor-pointer hover:border-[#A8A29E]">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function StepIntro({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="w-10 h-10 rounded-xl bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] mb-3">
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <h2 className="font-display font-bold text-2xl tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-[#57534E]">{subtitle}</p>}
    </div>
  );
}
